var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
// eslint-disable-next-line import/exports-last
export const SUPPORTED_TYPES = [
    "primary",
    "negative",
    "secondary",
    "tertiary-inverted",
    "tertiary",
    "text",
    "text-flat",
    "text-inverted",
];
let WhButtonElement = class WhButtonElement extends LitElement {
    constructor() {
        super(...arguments);
        this.variant = "primary";
        this.size = "medium";
        this.disabled = false;
        this.href = "";
        this.fullwidth = false;
    }
    get supportedVariant() {
        if (!SUPPORTED_TYPES.includes(this.variant)) {
            // eslint-disable-next-line no-console
            console.warn(`${this.variant} is not a supported option. Use  one of ${SUPPORTED_TYPES.join(",")} instead`);
            return "primary";
        }
        const map = {
            text: "text",
            "text-inverted": "text",
            "text-flat": "text",
            tertiary: "neutral",
            negative: "danger",
            "tertiary-inverted": "primary",
            secondary: "primary",
            primary: "primary",
        };
        return map[this.variant];
    }
    isOutlined() {
        return ["tertiary", "tertiary-inverted", "negative"].includes(this.variant);
    }
    isPill() {
        return !["text", "text-inverted", "text-flat"].includes(this.variant);
    }
    render() {
        const { variant, fullwidth, disabled, supportedVariant, size, href } = this;
        const classes = { [variant]: true, fullwidth };
        return html ` <sl-button
      exportparts="base"
      class=${classMap(classes)}
      .disabled=${disabled}
      ?pill=${this.isPill()}
      .variant=${supportedVariant}
      size=${size}
      .href=${href}
      .outline=${this.isOutlined()}
    >
      <slot name="prefix" slot="prefix"></slot>
      <slot></slot>
      <slot name="suffix" slot="suffix"></slot>
    </sl-button>`;
    }
};
WhButtonElement.styles = css `
    :host {
      --wh-dimension-radius-m: 8px;
      --wh-dimension-text-btn-border-radius: 10px;
    }

    sl-button::part(base) {
      align-items: center;
    }

    .primary::part(base),
    .text-inverted::part(base) {
      background-color: var(--wh-theme-colors-primary, gray);
      color: var(--wh-theme-colors-on-primary, white);
      border: none;
    }

    .primary::part(base):hover {
      background-color: var(--wh-theme-colors-primary-weak, gray);
    }

    .secondary::part(base) {
      background-color: var(--wh-theme-colors-secondary, lightblue);
      border: none;
      color: var(--wh-theme-colors-primary-strong, blue);
    }

    .secondary::part(base):hover {
      background-color: var(--wh-theme-colors-secondary-weak, lightblue);
      border: none;
      color: var(--wh-theme-colors-primary-strong, blue);
    }

    .tertiary::part(base) {
      background-color: transparent;
      border: 1px solid var(--wh-theme-colors-tertiary, blue);
      color: var(--wh-theme-colors-tertiary, blue);
    }

    .tertiary::part(base):hover {
      border-color: var(--wh-theme-colors-primary-strong, blue);
      color: var(--wh-theme-colors-primary-strong, blue);
    }

    .tertiary-inverted::part(base) {
      background-color: transparent;
      border-color: var(--wh-palette-colors-gray0, gray);
      border: 1px solid var(--wh-theme-colors-tertiary, blue);
      color: var(--wh-palette-colors-white, white);
    }

    .tertiary-inverted::part(base):hover {
      background-color: transparent;
      border-color: var(--wh-palette-colors-gray0, gray);
      color: var(--wh-palette-colors-white, white);
    }

    .primary::part(base)[disabled] {
      color: var(--wh-theme-colors-disabled, gray);
    }

    :is(.text, .text-flat)::part(base) {
      color: var(--wh-theme-colors-primary, darkblue);
    }

    :is(.text, .text-inverted, .text-flat)::part(base) {
      height: auto;
      line-height: 1.5;
      padding: 4px 12px;
    }

    :is(.text, .text-inverted, .text-flat)::part(label) {
      padding-inline-start: 0;
      padding-inline-end: 0;
    }

    .text::part(base):hover,
    .text-inverted::part(base):hover {
      border-radius: var(--wh-dimension-text-btn-border-radius);
    }

    .text::part(base):hover {
      background-color: var(--wh-theme-colors-background, #e5e5e5);
    }

    .text-inverted::part(base):hover {
      background-color: var(--wh-theme-colors-on-surface, hotpink);
    }

    .text::part(base):active {
      background-color: var(--wh-theme-colors-on-background, #d6d6da);
    }

    sl-button.fullwidth {
      width: 100%;
    }

    slot[name="prefix"]::slotted(*) {
      padding-inline-end: 12px;
    }

    slot[name="suffix"]::slotted(*) {
      padding-inline-start: 12px;
    }
  `;
__decorate([
    property({ type: String })
], WhButtonElement.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], WhButtonElement.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WhButtonElement.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], WhButtonElement.prototype, "href", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WhButtonElement.prototype, "fullwidth", void 0);
WhButtonElement = __decorate([
    customElement("wh-button")
], WhButtonElement);
export { WhButtonElement };
